import clsx from 'clsx'
import { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BasicStyledComponent, DSPoll, DSPostContext } from 'shared-definitions/types'
import { AuthContext } from '../../contexts/AuthContext'
import { PostContext } from '../../contexts/PostContext'
import { googleSignInIntent } from '../../lib/google-sign-in-intent'
import PollFootnote, {
  MineMark,
  removableChoice,
  removeVoteLabel,
  useCanRemoveVote,
} from '../../polls/PollFootnote'
import { usePoll, usePollVote } from '../../polls/PollStoreContext'
import { PollState, pollTotal } from '../../polls/poll-store'
import { commentSnippet, commentsLabel, timeAgo } from '../comment-format'
import { ConversationContext, ConversationOpenOptions } from './ConversationContext'
import { ASK_LINE, campLine, POSTED_LINE, SIGN_IN_TO_SAY_WHY } from '../../polls/ask-copy'
import { PICK_TARGET_ATTR } from '../../polls/pick-flight'
import {
  choicePercent,
  pickLabel,
  PickTag,
  SAY_WHY_PLACEHOLDER,
  trimLabel,
  votesLabel,
} from './ConversationPoll'
import styles from './ConversationRailCard.module.css'
import HighlightLabel from '../HighlightLabel'
import PinMark from '../PinMark'
import { ConversationPreview, useConversationPreview } from './useConversationPreview'

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/** The custom property the rail ads add to their sticky `top` (`SidebarWithStickyAds.module.css`). */
const PINNED_OFFSET_VAR = '--rail-pinned-offset'
/** Gap between the stuck card and the ad pinned under it. */
const PINNED_GAP_PX = 16

/**
 * The rail card's poll block (2026-10-24; one voted form 2026-10-28), from what the reader did
 * and what the preview knows: `vote` until the reader voted (the server HTML, for every visitor);
 * `voted` after any vote — the question, one split bar, the reader's pick row and "All N
 * results", which expands the full list in place (seven options at card width ran past a laptop
 * viewport, so the bars are no longer drawn by default). A vote from an earlier visit keeps the
 * vote form until the preview has settled (`settled`: ready, error or unavailable): the ask under
 * the block reads the preview's camps, so a returning voter's card changes once, not twice. A
 * vote made on this page (`fresh`) shows at once.
 */
export type RailPollMode = 'vote' | 'voted'

export function railPollMode(params: {
  voted: boolean
  fresh: boolean
  settled: boolean
}): RailPollMode {
  return params.voted && (params.fresh || params.settled) ? 'voted' : 'vote'
}

/** "N votes · results after you vote" before a vote; null at 0 (never "0 votes"). */
export function railVoteCountLine(total: number): string | null {
  return total > 0 ? `${votesLabel(total)} · results after you vote` : null
}

/** Dense choices from this many options up… */
export const RAIL_POLL_DENSE_MIN_CHOICES = 5
/** …or from the first label longer than this (one line of the rail's 14px bold is ~34 chars). */
export const RAIL_POLL_LONG_LABEL = 36

/**
 * Whether the vote form draws its choices dense (2026-10-28): left-aligned, 13px, 36px rows. The
 * full-size form is sized for a Yes/No question — AA's Pixel loyalty poll (seven two-line
 * choices) grew the card past a laptop viewport, with the highlighted comment below the fold and
 * the pinned ads off-screen.
 */
export function railPollDense(choices: readonly { label: string }[]): boolean {
  return (
    choices.length >= RAIL_POLL_DENSE_MIN_CHOICES ||
    choices.some(choice => choice.label.length > RAIL_POLL_LONG_LABEL)
  )
}

/** The choices the collapsed vote form shows before "N more options". */
export const RAIL_POLL_SHOWN = 3

/**
 * Whether the vote form collapses (2026-10-28, Guillaume's option D): five options or more draw
 * the first three and a "N more options" row that expands the rest in place — the card grows,
 * the pinned ads re-measure. Four or fewer draw in full (a "1 more" row would hide one option
 * to save one row). Once the reader voted, "All N results" draws every choice (`RailPoll`).
 */
export function railPollCollapsed(choices: readonly unknown[]): boolean {
  return choices.length >= RAIL_POLL_DENSE_MIN_CHOICES
}

/** "4 more options" / "1 more option". */
export function moreOptionsLabel(hidden: number): string {
  return `${hidden} more option${hidden === 1 ? '' : 's'}`
}

/** "All 7 results" (the voted block's expander). */
export function allResultsLabel(count: number): string {
  return `All ${count} result${count === 1 ? '' : 's'}`
}

interface RailPollProps {
  poll: DSPoll
  state: PollState
  mode: RailPollMode
  postMeta: DSPostContext | null
  /** The reader's last vote on this page (`lastVote.seq`); above 0 the bars fill, again per vote. */
  fillKey: number
  /** A choice was tapped (the card moves focus to its field if the block loses the button). */
  onTap: () => void
}

/**
 * The poll at the top of the rail card. Votes go through the one poll store (`usePollVote`,
 * `votedFrom: 'rail'`), so the article's poll and the panel move with it, and a signed-out
 * re-tap asks to sign in exactly as there. Mounted only when the article has a conversation poll.
 *
 * One tree for both modes, slot by slot (a null keeps its place), so the choices list keeps its
 * buttons across a change of mode: a tap on the own choice in the expanded results removes the
 * vote and the button keeps focus in the vote form.
 */
const RailPoll: FC<RailPollProps> = ({ poll, state, mode, postMeta, fillKey, onTap }) => {
  const vote = usePollVote(poll, 'rail', postMeta)
  const canRemove = useCanRemoveVote()
  // "N more options" tapped (the block is keyed by the poll: a new poll starts collapsed).
  const [expanded, setExpanded] = useState(false)
  // "All N results" tapped — its own state, so a vote never opens the results by itself; once
  // tapped, the vote form after a removal draws every choice too (the tapped button stays).
  const [allResults, setAllResults] = useState(false)
  const total = pollTotal(state)
  const fill = fillKey > 0
  const voted = mode === 'voted'
  const mine = voted ? state.choices.find(choice => choice.id === state.myChoiceId) ?? null : null
  const showList = !voted || allResults
  const countLine = voted ? (total > 0 && votesLabel(total)) || null : railVoteCountLine(total)
  const dense = railPollDense(state.choices)
  const collapsed = !voted && !expanded && !allResults && railPollCollapsed(state.choices)
  const choices = collapsed ? state.choices.slice(0, RAIL_POLL_SHOWN) : state.choices
  const minePercent = mine ? choicePercent(mine.value, total) : 0
  const mineLabel = mine ? trimLabel(mine.label) : ''
  return (
    <div className={clsx(styles.poll, fill && styles.pollFill)}>
      {voted ? null : <span className={styles.pollLabel}>{"Readers' poll"}</span>}
      <p className={clsx(styles.pollQuestion, voted && styles.pollQuestionCompact)}>
        {state.question}
      </p>
      {voted ? (
        // Keyed by the vote: the fill plays again on a changed vote.
        <div key={fillKey} className={styles.split} aria-hidden="true">
          {state.choices
            .filter(choice => choice.value > 0)
            .map(choice => (
              <span
                key={choice.id}
                className={clsx(styles.splitPart, {
                  [styles.splitMine]: choice.id === state.myChoiceId,
                })}
                style={{ flexGrow: choice.value }}
              />
            ))}
        </div>
      ) : null}
      {mine ? (
        <div
          className={styles.pick}
          role="group"
          aria-label={`Your pick: ${mine.label}, ${minePercent}%`}
        >
          <span className={styles.pickText}>
            <span
              className={styles.pickLabel}
              title={mineLabel === mine.label ? undefined : mine.label}
            >
              {mineLabel}
            </span>
            {/* The article poll's "Your pick" chip lands here (`flyPick`). */}
            <span className={styles.pickChip} {...{ [PICK_TARGET_ATTR]: poll.id }}>
              <svg
                className={styles.pickCheck}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                aria-hidden="true"
              >
                <path d="M5 12l5 5 9-10" />
              </svg>
              {'Your pick'}
            </span>
          </span>
          <span className={styles.pickPercent}>{`${minePercent}%`}</span>
        </div>
      ) : null}
      {showList ? (
        <div
          className={clsx(styles.pollChoices, {
            [styles.pollChoicesDense]: dense,
            // Two long labels side by side would wrap into a column each: dense stacks them.
            [styles.pollChoicesPair]: !voted && !dense && state.choices.length === 2,
          })}
        >
          {choices.map(choice => {
            const isMine = choice.id === state.myChoiceId
            const percent = choicePercent(choice.value, total)
            const label = trimLabel(choice.label)
            return (
              <button
                key={choice.id}
                type="button"
                aria-pressed={isMine}
                aria-label={removeVoteLabel(choice.label, isMine, canRemove)}
                // A cut label: the whole sentence on hover (the panel's poll draws it in full).
                title={label === choice.label ? undefined : choice.label}
                className={clsx(styles.pollChoice, removableChoice(isMine, canRemove), {
                  [styles.pollChoiceDense]: dense,
                  [styles.pollChoiceResult]: voted,
                  [styles.pollChoiceMine]: isMine,
                  [styles.pollChoiceBusy]: state.voting,
                })}
                onClick={() => {
                  onTap()
                  void vote(choice.id)
                }}
              >
                {voted ? (
                  // Keyed by the vote (not the button: it keeps focus): the fill plays again.
                  <span
                    key={fillKey}
                    className={styles.pollBar}
                    style={{ transform: `scaleX(${percent / 100})` }}
                    aria-hidden="true"
                  />
                ) : null}
                <span className={styles.pollChoiceLabel}>
                  {label}
                  {isMine ? <MineMark className={styles.pollMine} canRemove={canRemove} /> : null}
                </span>
                {voted ? <span className={styles.pollPercent}>{`${percent}%`}</span> : null}
              </button>
            )
          })}
          {collapsed ? (
            <button
              type="button"
              className={styles.pollMore}
              aria-expanded={false}
              onClick={() => setExpanded(true)}
            >
              {moreOptionsLabel(state.choices.length - RAIL_POLL_SHOWN)}
              <svg
                className={styles.pollMoreIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          ) : null}
        </div>
      ) : (
        // Expands the list above in place and goes (the card's ResizeObserver re-pins the ads).
        <button
          type="button"
          className={styles.allResults}
          aria-expanded={false}
          onClick={() => setAllResults(true)}
        >
          {allResultsLabel(state.choices.length)}
          <span aria-hidden="true">{' ›'}</span>
        </button>
      )}
      {countLine ? <span className={styles.pollCount}>{countLine}</span> : null}
      <PollFootnote poll={poll} state={state} from="rail" postMeta={postMeta} hint={showList} />
    </div>
  )
}

/** The preview slot's content per fetch state (the slot is not drawn for an empty thread).
 *  `pollState` (voted poll only): the quoted author's pick drawn after the name. */
function previewSlot(preview: ConversationPreview, pollState: PollState | null): ReactNode {
  if (preview.status === 'idle' || preview.status === 'loading') {
    return null
  }
  const ready = preview.status === 'ready'
  const highlight = ready ? preview.highlight : null
  const quoted = highlight?.comment ?? (ready ? preview.newest : null)
  if (!quoted) {
    // error / unavailable, or a total with no live top-level comment to quote.
    return <span className={styles.body}>{'Read what readers said'}</span>
  }
  // The panel's highlight (featured, else top); without one, the newest. The last hour's
  // activity (`newInLastHour`) is the mobile bar's, not drawn here.
  const pick = pollState ? pickLabel(pollState, quoted.author.pick) : null
  // The pin beside the name: a pinned or featured comment the label is NOT already calling the
  // highlight (kind 'featured' reads "Pinned" itself) — i.e. the newest, quoted for want of a pick.
  const pinned = (quoted.pinned || quoted.featured) && highlight?.kind !== 'featured'
  return (
    <>
      <HighlightLabel kind={highlight ? highlight.kind : 'latest'} className={styles.label} />
      <span className={styles.meta}>
        {quoted.author.name ?? 'Anonymous'}
        {pinned ? (
          <>
            {' '}
            <PinMark />
          </>
        ) : null}
        {pick ? (
          <>
            {' '}
            <PickTag label={pick} />
          </>
        ) : null}
        <span className={styles.time}>{` · ${timeAgo(quoted.createdAt)}`}</span>
      </span>
      <span className={styles.body}>{commentSnippet(quoted.body)}</span>
    </>
  )
}

/**
 * Desktop right-rail entry to the conversation panel: count, the highlighted comment (FEATURED /
 * TOP COMMENT — the panel's first — else the LATEST), "Add your take…" (opens with the composer focused) and "Read all".
 *
 * It travels with the reader in full: the root is `position: sticky` at the rail's stack line
 * (`--rail-stack-top`, PostNavigation: under the under-nav table of contents when it shows, else
 * rem(64) — railStickyStack.ts), a child of `<PostNavigation>` (which is as tall as the article
 * from `bp(lg)`) placed after every WordPress sidebar widget and right before the sticky ads
 * (`SidebarFloatingAd`), so the widgets scroll away above it and the ads pin under it. The card
 * never changes shape with scroll (Guillaume, 2026-10-27: the stuck card used to collapse to a
 * header + field strip, and twice collapsed at the top of the page on first load — the whole
 * stuck/collapse machinery is gone, not patched a third time). The rail ads stick
 * `--rail-pinned-offset` below the same line: the card's measured height + 16px, written onto
 * its parent rail in a layout effect and kept current by a ResizeObserver on the card (the poll
 * block changes height with a vote; the preview slot has one height in every fetch state).
 *
 * Server-rendered: "is there a conversation" comes from `PostContext` (`engagementContainerId`,
 * not sponsored — the same rule as `ArticleConversation`), not from `ConversationContext`, whose
 * container only registers after mount. The preview slot has one height in
 * every fetch state (CSS `min-height`), so neither hydration nor the fetch landing shifts the
 * rail. Clicks open the panel once the host has registered; before that (or with no host) they
 * jump to `#comments-section`.
 */
const ConversationRailCard: FC<BasicStyledComponent> = ({ className }) => {
  const post = PostContext.useOptionalContainer()
  const conversation = ConversationContext.useOptionalContainer()
  // "Add your take…" stands in for the composer: signed out, its panel field opens the sign-in.
  const auth = AuthContext.useOptionalContainer()
  const { onPointerDown: prewarmOnPress, onFocus: prewarmOnFocus } = googleSignInIntent(
    auth?.prewarmGoogle
  )
  // Signed out, once the session check has answered (`authResolved`: a signed-in returning voter
  // never reads "Sign in to say why" first); no auth container means no sign-in to ask for.
  const signedOut = Boolean(auth?.authResolved && !auth.authState)
  const containerId =
    post && !post.sponsored && post.engagementContainerId ? post.engagementContainerId : null
  const preview = useConversationPreview(containerId)
  // Polls in the conversation (2026-10-24): the article's toggled poll, its live state from the
  // one poll store. The server and the first client render draw the page data (the vote mode,
  // the same for every visitor); the reader's own vote applies after hydration.
  const poll = (containerId && post?.conversationPoll) || null
  const pollState = usePoll(poll)
  const total = preview.status === 'ready' ? preview.total : null
  const pollVoted = pollState !== null && pollState.myChoiceId !== null
  const pollMode = pollState
    ? railPollMode({
        voted: pollVoted,
        fresh: pollState.lastVote !== null,
        settled: preview.status !== 'idle' && preview.status !== 'loading',
      })
    : null
  // Voted and not yet posted with the pick: the field is the ask ("Say why…"), wherever the
  // vote came from; a vote cast on this page (`lastVote`) also rings and pulses it, once per vote.
  const asking = pollMode === 'voted' && pollState !== null && !pollState.posted
  const nudging = asking && Boolean(pollState?.lastVote)
  let askText: ReactNode = null
  if (pollMode === 'voted' && pollState) {
    const mine = pollState.choices.find(choice => choice.id === pollState.myChoiceId)
    const threads =
      preview.status === 'ready'
        ? preview.camps?.find(camp => camp.choiceId === pollState.myChoiceId)?.threads ?? null
        : null
    askText = pollState.posted ? (
      POSTED_LINE
    ) : (
      <>
        <strong className={styles.askLead}>{ASK_LINE}</strong>{' '}
        <span className={styles.askCamp}>
          {campLine(threads, mine ? choicePercent(mine.value, pollTotal(pollState)) : 0)}
        </span>
      </>
    )
  }
  const cardRef = useRef<HTMLElement>(null)
  const takeRef = useRef<HTMLButtonElement>(null)
  // A rail vote unmounts the tapped button (the voted block draws no buttons until "All N
  // results"): the caret's next stop is the field ("Say why…"), not the top of the page.
  const pollTapped = useRef(false)
  useEffect(() => {
    if (!pollTapped.current || pollMode !== 'voted') {
      return
    }
    pollTapped.current = false
    const active = document.activeElement
    if (!active || active === document.body) {
      takeRef.current?.focus()
    }
  }, [pollMode])
  useIsoLayoutEffect(() => {
    const card = cardRef.current
    const rail = card?.parentElement
    if (!card || !rail) {
      return undefined
    }
    // The ads pin under the full card. In the first screen they are in flow below it and reach
    // their sticky line only once the card is stuck.
    const publish = (): void => {
      rail.style.setProperty(PINNED_OFFSET_VAR, `${card.offsetHeight + PINNED_GAP_PX}px`)
    }
    publish()
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    resized?.observe(card)
    return () => {
      resized?.disconnect()
      rail.style.removeProperty(PINNED_OFFSET_VAR)
    }
  }, [containerId])

  if (!containerId) {
    return null
  }

  const open = (options?: ConversationOpenOptions): void => {
    if (conversation?.containerId) {
      conversation.open('rail', options)
      return
    }
    document.getElementById('comments-section')?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }
  // Empty thread (2026-10-24, tightened 2026-10-27 — Guillaume): "Conversation" alone in the
  // header (no count, no "Open"), no preview slot at all — just the field, whose label carries
  // the ask ("Vote, then be the first to say why…" under an unvoted poll) — and every click opens
  // with the composer focused. The card tightens once, when the preview answers 0 (the pinned
  // ads re-measure through the card's ResizeObserver). Never a "0": the count pill is drawn only
  // once the preview knows a total above zero.
  const empty = total === 0
  const openOptions: ConversationOpenOptions | undefined = empty
    ? { focusComposer: true }
    : undefined
  const readAll = total !== null ? `Read all ${commentsLabel(total)}` : 'Read all comments'

  let takeLabel = 'Add your take…'
  if (empty) {
    takeLabel =
      poll && !pollVoted ? 'Vote, then be the first to say why…' : 'Be the first to comment…'
  }
  if (asking) {
    takeLabel = signedOut ? SIGN_IN_TO_SAY_WHY : SAY_WHY_PLACEHOLDER
  }
  // The pulse's keyframes alternate per vote, so a changed vote plays it again without re-keying
  // the field (it may hold the focus).
  const pulse = nudging && pollState?.lastVote ? pollState.lastVote.seq % 2 : null

  return (
    <section
      ref={cardRef}
      className={clsx(styles.card, className)}
      aria-label="Conversation preview"
    >
      <button type="button" className={styles.header} onClick={() => open(openOptions)}>
        <span className={styles.title}>
          <span className={styles.titleText}>{'Conversation'}</span>
          {total ? <span className={styles.count}>{total}</span> : null}
        </span>
        {empty ? null : (
          <span className={styles.openLink}>
            {'Open'}
            <svg
              className={styles.openIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              aria-hidden="true"
            >
              <path d="M7 17L17 7M9 7h8v8" />
            </svg>
          </span>
        )}
      </button>

      {poll && pollState && pollMode ? (
        <RailPoll
          key={poll.id}
          poll={poll}
          state={pollState}
          mode={pollMode}
          postMeta={post}
          fillKey={pollState.lastVote?.seq ?? 0}
          onTap={() => {
            pollTapped.current = true
          }}
        />
      ) : null}

      {empty ? null : (
        <div className={styles.section}>
          {previewSlot(preview, pollMode === 'voted' ? pollState : null)}
        </div>
      )}

      <div className={styles.actions}>
        {/* An empty thread has no ask paragraph: the field's label carries it (06afbd638). */}
        {askText && !empty ? <p className={styles.ask}>{askText}</p> : null}
        <button
          ref={takeRef}
          type="button"
          className={clsx(styles.take, {
            [styles.takeAsk]: nudging,
            [styles.takePulseA]: pulse === 1,
            [styles.takePulseB]: pulse === 0,
          })}
          onPointerDown={prewarmOnPress}
          onFocus={prewarmOnFocus}
          onClick={() => open({ focusComposer: true })}
        >
          {takeLabel}
        </button>
        {empty ? null : (
          <button type="button" className={styles.readAll} onClick={() => open()}>
            {readAll}
            <span aria-hidden="true">{'→'}</span>
          </button>
        )}
      </div>
    </section>
  )
}

export default ConversationRailCard
